import { TCalculatorData, TCalculatorTopUpData, TCommonData, TextInputEnum, TInputData, TTextData, TTimerButtonData, TWidget } from '@libs/types';
import React from 'react';
import {
    TBalanceData,
    TBar,
    TCarouselData,
    TConsumerListData,
    TKYC,
    TMpiData,
    TCardBalanceData,
    TCardView,
    TConsumerInputPhoneData,
    TInputSearchAddress,
    TButtonRowEmphasised,
    TRowActionData,
    TGooglePay,
    TApplePay,
    TDetailsSeparateTextData,
    TBrazeBanner,
    TWebViewLayout,
    TSelectorRegularData,
    TInputPasscodeData,
} from '../types/data';
import {
    ActionWidgetEnum,
    AnimationIconEnum,
    BarEnum,
    ButtonEnum,
    CalculatorEnum,
    CardEnum,
    CarouselEnum,
    CheckboxEnum,
    DropdownEnum,
    ImageEnum,
    KycEnum,
    LayoutEnum,
    ListEnum,
    MpiEnum,
    RowEnum,
    SeparatorEnum,
    StoriesEnum,
    InputEnum,
    TextEnum,
    WidgetEnum,
} from '../types/enums';
import {
    TButtonOption,
    TListOption,
    TInputOption,
    TRowOption,
    TBrazeOption,
    TWebViewLayoutOption,
    TDetailsSeparateTextOption,
    TOTPOption,
    TSelectorRegularOption,
    TPasscodeOption,
} from '../types/options';
import { Action } from '../widgets/Action/Action';
import { LimitBar } from '../widgets/Bars/LimitBar';
import { ButtonMenu } from '../widgets/Buttons/ButtonMenu';
import { Calculator } from '../widgets/Calculactor/Calculator';
import { CalculatorTopUp } from '../widgets/Calculactor/CalculatorTopUp';
import { CardInput } from '../widgets/Card/CardInput';
import { CardBalance } from '../widgets/Card/CardBalance/CardBalance';
import { CardView } from '../widgets/Card/CardView';
import { Checkbox } from '../widgets/Checkbox/Checkbox';
import { Switcher } from '../widgets/Checkbox/Switcher';
import { Visual } from '../widgets/Image/Visual';
import { ProfileImage } from '../widgets/Image/profile/ProfileImage';
import { InputDate } from '../widgets/Inputs/InputDate';
import { InputPasscode } from '../widgets/Inputs/InputPasscode';
import { InputPhone } from '../widgets/Inputs/InputPhone';
import { InputSearchAddress } from '../widgets/Inputs/InputSearchAddress';
import { InputText } from '../widgets/Inputs/InputText';
import { OtpInput } from '../widgets/Inputs/OtpInput';
import { StatusTracker } from '../widgets/List/StatusTracker';
import { Mpi } from '../widgets/Mpi/Mpi';
import { Separator } from '../widgets/Separator/Separator';
import { Balance } from '../widgets/Text/Balance';
import { TextLinked } from '../widgets/Text/TextLinked';
import { AppVersion } from '../widgets/Text/AppVersion';
import { SelectorRegular } from '../widgets/UI/Row/SelectorRegular';
import { TextCommon } from '../widgets/UI/Text/TextCommon';
import { Carousel } from '../widgets/Carousel/Carousel';
import { Dropdown } from '../widgets/Dropdown/Dropdown';
import { RowEmphasised } from '../widgets/Buttons/RowEmphasised';
import { RowAction } from '../widgets/Buttons/RowAction';
import { TimerButton } from '../widgets/Buttons/TimerButton';
import { ButtonRowTimer } from '../widgets/Buttons/ButtonRowTimer';
import { ButtonBanner } from '../widgets/Buttons/ButtonBanner';
import { ButtonMessage } from '../widgets/Buttons/ButtonMessage';
import { ButtonCircleAnimated } from '../widgets/Buttons/ButtonCircleAnimated';
import { ButtonGooglePay } from '../widgets/Buttons/ButtonGooglePay';
import { ButtonApplePay } from '../widgets/Buttons/ButtonApplePay';
import { DetailsSeparateText } from '../widgets/Buttons/DetailsSeparateText';
import { ButtonPill } from '../widgets/Buttons/ButtonPill';
import { ButtonCircle } from '../widgets/Buttons/ButtonCircle';
import { ButtonRow } from '../widgets/Buttons/ButtonRow';
import { Sumsub } from '../widgets/KYC/Sumsub';
import { ZendeskChat } from '../widgets/KYC/ZendeskChat';
import { SuccessAnimation } from '../widgets/Animation/SuccessAnimation';
import { ErrorAnimation } from '../widgets/Animation/ErrorAnimation';
import { LoadingAnimation } from '../widgets/Animation/LoadingAnimation';
import { PromoStories } from '../widgets/Stories/PromoStories';
import { BrazeBanner } from '../widgets/Stories/BrazeBanner';
import { ListSelect } from '../widgets/List/select/ListSelect';
import { ListTransactions } from '../widgets/List/ListTransactions';
import { ListContacts } from '../widgets/List/ListContacts';
import { ListTemplate } from '../widgets/List/ListTemplate';
import { WebViewLayout } from '../widgets/layout/WebViewLayout';
import { SeparateLayoutTransition } from '../widgets/layout/SeparateLayoutTransition';
import { VerticalLayout } from '../widgets/layout/VerticalLayout';
import { VerticalLayoutWide } from '../widgets/layout/VerticalLayoutWide';
import { HorizontalLayout } from '../widgets/layout/HorizontalLayout';
import { FloatingLayout } from '../widgets/layout/FloatingLayout';

export const getWidgetById = (widgetId: WidgetEnum & TextInputEnum, widget: TWidget<TCommonData>, Package: React.ElementType) => {
    switch (widgetId) {
        case TextEnum.BALANCE:
            return <Balance widget={widget as TWidget<TBalanceData>} />;
        case TextEnum.TEXT:
            return <TextCommon isWidget={true} widget={widget as TWidget<TTextData>} />;
        case TextEnum.TEXT_LINKED:
            return <TextLinked options={widget.options} data={widget.data as TTextData} uid={widget.uid} />;
        case TextEnum.APP_VERSION:
            return <AppVersion widget={widget} />;
        case LayoutEnum.WRAPPER_LAYOUT:
            return <Package uids={widget.widgets} widgets={widget.widgets} />;
        case LayoutEnum.VERTICAL_LAYOUT:
            return (
                <VerticalLayout widget={widget} margin={widget.options?.margin}>
                    <Package uids={widget.widgets} widgets={widget.widgets} />
                </VerticalLayout>
            );
        case LayoutEnum.VERTICAL_LAYOUT_WIDE:
            return (
                <VerticalLayoutWide widget={widget} margin={widget.options?.margin}>
                    <Package uids={widget.widgets} widgets={widget.widgets} />
                </VerticalLayoutWide>
            );
        case LayoutEnum.HORIZONTAL_LAYOUT:
            return (
                <HorizontalLayout widget={widget} margin={widget.options?.margin}>
                    <Package uids={widget.widgets} widgets={widget.widgets} />
                </HorizontalLayout>
            );
        case LayoutEnum.FLOATING_LAYOUT:
            return (
                <FloatingLayout widget={widget} margin={widget.options?.margin}>
                    <Package uids={widget.widgets} widgets={widget.widgets} />
                </FloatingLayout>
            );
        case LayoutEnum.SEPARATE_LAYOUT:
        case LayoutEnum.CALCULATOR_CONTENT_LAYOUT:
            return (
                <SeparateLayoutTransition
                    widget={widget}
                    topWidget={<Package uids={widget.widgets?.[0] ? [widget.widgets[0]] : []} />}
                    bottomWidget={<Package uids={widget.widgets?.[1] ? [widget.widgets[1]] : []} />}
                />
            );
        case LayoutEnum.WEB_VIEW_LAYOUT:
            return <WebViewLayout widget={widget as TWidget<TWebViewLayout, TWebViewLayoutOption>} />;
        case AnimationIconEnum.Success:
            return <SuccessAnimation widget={widget} />;
        case AnimationIconEnum.Error:
            return <ErrorAnimation widget={widget} />;
        case AnimationIconEnum.Loading:
            return <LoadingAnimation isWidget widget={widget} />;
        case CarouselEnum.CAROUSEL:
            return <Carousel widget={widget as TWidget<TCarouselData>} />;
        case InputEnum.STROKED:
            return <InputText widget={widget} />;
        case InputEnum.PHONE_TEXT_INPUT:
            return <InputPhone widget={widget as TWidget<TConsumerInputPhoneData, TInputOption>} />;
        case InputEnum.DATE_MASK_TEXT_INPUT:
            return <InputDate widget={widget} />;
        case TextInputEnum.ENTERPASSCODE:
            return <InputPasscode widget={widget as TWidget<TInputPasscodeData, TPasscodeOption>} />;
        case InputEnum.AUTO_SUBMIT_TEXT_INPUT:
            return <InputText autoComplete="one-time-code" widget={widget} />;
        case InputEnum.SEARCH_ADDRESS_INPUT:
            return <InputSearchAddress widget={widget as TWidget<TInputSearchAddress, TInputOption>} />;
        case InputEnum.OTP_INPUT:
            return <OtpInput widget={widget as TWidget<TInputData, TOTPOption>} />;
        case CardEnum.CARD_INPUT:
            return <CardInput widget={widget} />;
        case CardEnum.CARD_BALANCE:
            return <CardBalance widget={widget as TWidget<TCardBalanceData>} />;
        case CardEnum.CARD_VIEW:
            return <CardView widget={widget as TWidget<TCardView>} />;
        case ButtonEnum.BUTTON_PILL:
            return <ButtonPill isWidget={true} widget={widget} />;
        case ButtonEnum.BUTTON_CIRCLE:
            return <ButtonCircle isWidget={true} widget={widget} />;
        case ButtonEnum.BUTTON_ROW:
            return <ButtonRow as="button" isWidget={true} widget={widget} />;
        case ButtonEnum.ROW_EMPHASISED:
            return <RowEmphasised as="button" isWidget={true} widget={widget as TWidget<TButtonRowEmphasised, TRowOption>} />;
        case ButtonEnum.ROW_ACTION:
            return <RowAction as="button" isWidget={true} widget={widget as TWidget<TRowActionData, TRowOption>} />;
        case ButtonEnum.TIMER_BUTTON:
            return <TimerButton widget={widget as TWidget<TTimerButtonData, TButtonOption>} />;
        case ButtonEnum.BUTTON_ROW_TIMER:
            return <ButtonRowTimer widget={widget as TWidget<TTimerButtonData, TButtonOption>} />;
        case ButtonEnum.BUTTON_BANNER:
            return <ButtonBanner widget={widget} />;
        case ButtonEnum.BUTTON_MESSAGE:
            return <ButtonMessage widget={widget} />;
        case ButtonEnum.BUTTON_CIRCLE_ANIMATED:
            return <ButtonCircleAnimated widget={widget} />;
        case ButtonEnum.BUTTON_GOOGLE_PAY:
            return <ButtonGooglePay widget={widget as TWidget<TGooglePay>} />;
        case ButtonEnum.BUTTON_APPLE_PAY:
            return <ButtonApplePay widget={widget as TWidget<TApplePay>} />;
        case ButtonEnum.BUTTON_MENU:
            return <ButtonMenu widget={widget} />;
        case ButtonEnum.DETAILS_SEPARATE_TEXT:
            return <DetailsSeparateText widget={widget as TWidget<TDetailsSeparateTextData, TDetailsSeparateTextOption>} />;
        case CheckboxEnum.SWITCHER:
            return <Switcher isWidget={true} widget={widget} />;
        case CheckboxEnum.CHECKBOX:
            return <Checkbox isWidget={true} widget={widget} />;
        case DropdownEnum.DROPDOWN:
            return <Dropdown widget={widget} />;
        case ActionWidgetEnum.ACTION_WIDGET:
            return <Action widgetId={widgetId} widget={widget} />;
        case ListEnum.SELECT: {
            const data = (widget as TWidget<TConsumerListData, TListOption>).data;
            return <ListSelect widget={widget as TWidget<TConsumerListData, TListOption>} rowId={data.cellWidgetId ?? RowEnum.ROW_REGULAR} />;
        }
        case ListEnum.TRANSACTIONS:
            return <ListTransactions widget={widget as TWidget<TConsumerListData, TListOption>} />;
        case ListEnum.CURRENCY:
            return <ListSelect widget={widget as TWidget<TConsumerListData, TListOption>} rowId={RowEnum.SELECTOR_REGULAR} />;
        case ListEnum.CONTACTS:
            return <ListContacts widget={widget as TWidget<TConsumerListData, TListOption>} />;
        case ListEnum.PROFILE_MENU:
        case ListEnum.TEMPLATE:
            return <ListTemplate widget={widget as TWidget<TConsumerListData, TListOption>} Package={Package} />;
        case ListEnum.STATUS_TRACKER:
            return <StatusTracker widget={widget} />;
        case CalculatorEnum.CALCULATOR:
            return <Calculator widget={widget as TWidget<TCalculatorData>} />;
        case CalculatorEnum.TOP_UP:
            return <CalculatorTopUp widget={widget as TWidget<TCalculatorTopUpData>} />;
        case SeparatorEnum.SEPARATOR:
            return <Separator isWidget={true} widget={widget} />;
        case ImageEnum.VISUAL:
            return <Visual isWidget={true} widget={widget} />;
        case ImageEnum.PROFILE_IMAGE:
            return <ProfileImage widget={widget} />;
        case StoriesEnum.STORIES:
            return <PromoStories widget={widget} />;
        case StoriesEnum.BRAZE_BANNER:
            return <BrazeBanner widget={widget as TWidget<TBrazeBanner, TBrazeOption>} />;
        case MpiEnum.MPI:
            return <Mpi widget={widget as TWidget<TMpiData>} />;
        case KycEnum.SUMSUB:
            return <Sumsub widget={widget as TWidget<TKYC>} />;
        case KycEnum.ZENDESK_CHAT_WIDGET:
            return <ZendeskChat isWidget widget={widget as TWidget<TKYC>} />;
        case BarEnum.LIMIT_BAR:
            return <LimitBar widget={widget as TWidget<TBar>} />;
        case RowEnum.SELECTOR_REGULAR:
            return <SelectorRegular isWidget={true} widget={widget as TWidget<TSelectorRegularData, TSelectorRegularOption>} />;
        default:
            return false;
    }
};
